import { useMemo, useState } from 'react'
import Link from 'next/link'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import { Localized } from '@lib/glossary/types'

type Framework = 'kvkk' | 'dora'

interface ChecklistItem {
  id: string
  frameworks: Framework[]
  question: Localized
  why: Localized
  fixHref: string
  fixLabel: Localized
}

const items: ChecklistItem[] = [
  {
    id: 'mfa',
    frameworks: ['kvkk', 'dora'],
    question: {
      en: 'Is MFA enforced on every system that holds personal or critical data, including legacy apps and VPNs?',
      tr: 'Kişisel veya kritik veri barındıran her sistemde (eski uygulamalar ve VPN dahil) MFA zorunlu mu?',
    },
    why: {
      en: 'Both frameworks treat strong authentication as a baseline; DORA names it explicitly.',
      tr: 'Her iki çerçeve de güçlü kimlik doğrulamayı taban kabul eder; DORA bunu açıkça adıyla sayar.',
    },
    fixHref: '/sign/mfa',
    fixLabel: { en: 'Monosign MFA', tr: 'Monosign MFA' },
  },
  {
    id: 'sso',
    frameworks: ['kvkk', 'dora'],
    question: {
      en: 'Do users sign in through a central identity provider instead of per-app local accounts?',
      tr: 'Kullanıcılar uygulama başına yerel hesaplar yerine merkezî bir kimlik sağlayıcı üzerinden mi giriş yapıyor?',
    },
    why: {
      en: 'Local accounts escape policy, logging, and deprovisioning; auditors find them fast.',
      tr: 'Yerel hesaplar politika, loglama ve erişim kapatma süreçlerinin dışında kalır; denetçiler bunları hızla bulur.',
    },
    fixHref: '/sign/sso',
    fixLabel: { en: 'Monosign SSO', tr: 'Monosign SSO' },
  },
  {
    id: 'authz-matrix',
    frameworks: ['kvkk'],
    question: {
      en: 'Is there a current authorization matrix: who can access which personal-data system, by role?',
      tr: 'Güncel bir yetki matrisi var mı: hangi role hangi kişisel veri sistemine erişim tanımlı?',
    },
    why: {
      en: 'KVKK guidance expects documented, role-appropriate authorization that stays current.',
      tr: 'KVKK rehberleri, güncel tutulan ve role uygun, belgelenmiş yetkilendirme bekler.',
    },
    fixHref: '/sync/governance',
    fixLabel: { en: 'Monosync Governance', tr: 'Monosync Governance' },
  },
  {
    id: 'jml',
    frameworks: ['kvkk', 'dora'],
    question: {
      en: 'Is access revoked the same day someone leaves or changes roles (automatically, not by ticket)?',
      tr: 'Biri ayrıldığında veya rol değiştirdiğinde erişimi aynı gün (ticket ile değil, otomatik) kapanıyor mu?',
    },
    why: {
      en: 'Stale access from departed employees is a recurring audit finding under both frameworks.',
      tr: 'Ayrılan çalışanlardan kalan erişimler her iki çerçevede de tekrarlayan bir denetim bulgusudur.',
    },
    fixHref: '/sign/lifecycle',
    fixLabel: { en: 'Lifecycle automation', tr: 'Yaşam döngüsü otomasyonu' },
  },
  {
    id: 'priv-inventory',
    frameworks: ['kvkk', 'dora'],
    question: {
      en: 'Do you have a complete inventory of privileged accounts, including service accounts and shared admins?',
      tr: 'Servis hesapları ve ortak kullanılan yöneticiler dahil, ayrıcalıklı hesapların eksiksiz bir envanteri var mı?',
    },
    why: {
      en: 'You cannot control accounts you have not found; discovery is step one of any PAM audit.',
      tr: 'Bulmadığınız hesabı kontrol edemezsiniz; keşif her PAM denetiminin ilk adımıdır.',
    },
    fixHref: '/pam',
    fixLabel: { en: 'Monopam discovery', tr: 'Monopam keşif' },
  },
  {
    id: 'session-recording',
    frameworks: ['kvkk', 'dora'],
    question: {
      en: 'Are administrator sessions on critical systems recorded and reviewable?',
      tr: 'Kritik sistemlerdeki yönetici oturumları kaydediliyor ve incelenebiliyor mu?',
    },
    why: {
      en: 'Session records turn "who did what" from a guess into evidence; both audits and incidents need it.',
      tr: 'Oturum kayıtları "kim ne yaptı" sorusunu tahminden kanıta çevirir; hem denetim hem olay müdahalesi bunu ister.',
    },
    fixHref: '/pam/session-recording',
    fixLabel: { en: 'Session recording', tr: 'Oturum kaydı' },
  },
  {
    id: 'jit',
    frameworks: ['dora'],
    question: {
      en: 'Is privileged elevation time-bound and approval-gated instead of standing admin rights?',
      tr: 'Ayrıcalık yükseltme, kalıcı yönetici hakları yerine süre sınırlı ve onaya bağlı mı?',
    },
    why: {
      en: 'Standing privilege is the attack path DORA’s access provisions are written against.',
      tr: 'Kalıcı ayrıcalık, DORA erişim hükümlerinin hedef aldığı saldırı yoludur.',
    },
    fixHref: '/pam/jit-access',
    fixLabel: { en: 'Just-in-time access', tr: 'Tam zamanında erişim' },
  },
  {
    id: 'vendor-access',
    frameworks: ['kvkk', 'dora'],
    question: {
      en: 'Is third-party / vendor access time-bound, recorded, and free of shared credentials?',
      tr: 'Üçüncü taraf / tedarikçi erişimi süre sınırlı, kayıtlı ve ortak parolasız mı?',
    },
    why: {
      en: 'DORA regulates ICT third parties directly; KVKK holds you responsible for processors’ access.',
      tr: 'DORA, BT üçüncü taraflarını doğrudan düzenler; KVKK, veri işleyenlerin erişiminden sizi sorumlu tutar.',
    },
    fixHref: '/solutions/external-partners',
    fixLabel: { en: 'External & partner access', tr: 'Dış ve iş ortağı erişimi' },
  },
  {
    id: 'logs',
    frameworks: ['kvkk', 'dora'],
    question: {
      en: 'Are authentication and access logs tamper-evident and exportable to your SIEM?',
      tr: 'Kimlik doğrulama ve erişim logları kurcalamaya karşı korumalı ve SIEM’e aktarılabilir mi?',
    },
    why: {
      en: 'Logs are the evidence layer for every other control; if they can be edited, they prove nothing.',
      tr: 'Loglar diğer tüm kontrollerin kanıt katmanıdır; değiştirilebiliyorsa hiçbir şey kanıtlamaz.',
    },
    fixHref: '/learn/audit-log-siem-export',
    fixLabel: { en: 'Audit logs → SIEM', tr: 'Denetim logları → SIEM' },
  },
  {
    id: 'access-reviews',
    frameworks: ['kvkk', 'dora'],
    question: {
      en: 'Do managers periodically review and certify who has access to what, with documented results?',
      tr: 'Yöneticiler kimin neye erişimi olduğunu periyodik olarak gözden geçirip belgeli şekilde onaylıyor mu?',
    },
    why: {
      en: 'Reviews are the recurring, documented evaluation both frameworks expect of access controls.',
      tr: 'Gözden geçirmeler, her iki çerçevenin de erişim kontrollerinden beklediği periyodik ve belgeli değerlendirmedir.',
    },
    fixHref: '/sync/governance',
    fixLabel: { en: 'Access reviews', tr: 'Erişim gözden geçirmeleri' },
  },
  {
    id: 'anomaly',
    frameworks: ['dora'],
    question: {
      en: 'Would you notice a compromised account (new device, impossible travel, brute force) within hours?',
      tr: 'Ele geçirilmiş bir hesabı (yeni cihaz, imkânsız seyahat, kaba kuvvet) saatler içinde fark eder miydiniz?',
    },
    why: {
      en: 'DORA Article 10 expects prompt detection of anomalous activity.',
      tr: 'DORA 10. Madde, anormal aktivitenin hızla tespit edilmesini bekler.',
    },
    fixHref: '/sign',
    fixLabel: { en: 'Monosign signals', tr: 'Monosign sinyalleri' },
  },
  {
    id: 'reconstruction',
    frameworks: ['kvkk', 'dora'],
    question: {
      en: 'Could you reconstruct an incident (who accessed what, when) within your reporting deadline?',
      tr: 'Bir olayı (kim, neye, ne zaman erişti) bildirim süreniz içinde yeniden kurgulayabilir misiniz?',
    },
    why: {
      en: 'KVKK breach notice and DORA’s 24-hour early warning both assume fast reconstruction.',
      tr: 'KVKK ihlal bildirimi ve DORA’nın 24 saatlik erken uyarısı, hızlı yeniden kurgulamayı varsayar.',
    },
    fixHref: '/compliance/dora',
    fixLabel: { en: 'Incident evidence', tr: 'Olay kanıtları' },
  },
]

const ui = {
  seoTitle: {
    en: 'KVKK & DORA Identity Readiness Checklist (Free Tool) | Monofor',
    tr: 'KVKK ve DORA Kimlik Hazırlık Kontrol Listesi (Ücretsiz Araç) | Monofor',
  },
  seoDescription: {
    en: 'Free interactive checklist: score your identity and access controls against KVKK and DORA expectations, and see exactly which gaps to close first.',
    tr: 'Ücretsiz interaktif kontrol listesi: kimlik ve erişim kontrollerinizi KVKK ve DORA beklentilerine göre puanlayın, önce hangi eksikleri kapatacağınızı görün.',
  },
  eyebrow: { en: 'Free tool', tr: 'Ücretsiz araç' },
  title: {
    en: 'How audit-ready is your identity layer?',
    tr: 'Kimlik katmanınız denetime ne kadar hazır?',
  },
  lede: {
    en: 'Answer honestly for the framework that applies to you. Every unchecked box is a finding an auditor would write up, and a gap you can close.',
    tr: 'Size uygulanan çerçeve için dürüstçe yanıtlayın. İşaretlenmemiş her kutu, denetçinin rapora yazacağı bir bulgu ve kapatabileceğiniz bir eksik.',
  },
  frameworkLabel: { en: 'Framework', tr: 'Çerçeve' },
  checklistHeading: { en: 'The checklist', tr: 'Kontrol listesi' },
  scoreHeading: { en: 'Your readiness', tr: 'Hazırlık durumunuz' },
  gapsHeading: { en: 'Close these gaps first', tr: 'Önce bu eksikleri kapatın' },
  gapCta: { en: 'How Monofor helps:', tr: 'Monofor nasıl yardımcı olur:' },
  whyLabel: { en: 'Why it matters', tr: 'Neden önemli' },
  bandLow: {
    en: 'High audit risk: most identity controls are missing or undocumented.',
    tr: 'Yüksek denetim riski: kimlik kontrollerinin çoğu eksik veya belgesiz.',
  },
  bandMid: {
    en: 'Partial readiness: the foundations exist, but an auditor would still find material gaps.',
    tr: 'Kısmi hazırlık: temel var, ancak denetçi hâlâ önemli eksikler bulur.',
  },
  bandHigh: {
    en: 'Strong posture: keep evidence fresh and review this list quarterly.',
    tr: 'Güçlü durum: kanıtları güncel tutun ve bu listeyi üç ayda bir gözden geçirin.',
  },
  allDone: {
    en: 'No gaps for this framework. Nice work. Keep the evidence exportable.',
    tr: 'Bu çerçeve için eksik yok. Tebrikler. Kanıtları dışa aktarılabilir tutun.',
  },
  disclaimer: {
    en: 'This checklist covers the identity and access dimension of these frameworks only, and is general information rather than legal or audit advice.',
    tr: 'Bu kontrol listesi yalnızca bu çerçevelerin kimlik ve erişim boyutunu kapsar; hukuki veya denetim danışmanlığı değil, genel bilgidir.',
  },
  talkCta: { en: 'Talk with our experts', tr: 'Uzmanlarımızla görüşün' },
  complianceLink: {
    en: 'Read the full framework mapping',
    tr: 'Çerçevenin tam eşlemesini okuyun',
  },
}

function pick(v: Localized, locale: string) {
  return locale === 'tr' ? v.tr || v.en : v.en
}

type Props = { locale: string }

export default function IdentityComplianceChecklistPage({ locale }: Props) {
  const [framework, setFramework] = useState<Framework>('kvkk')
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const basePath = locale === 'tr' ? '/tr' : ''
  const p = (v: Localized) => pick(v, locale)

  const activeItems = useMemo(
    () => items.filter((i) => i.frameworks.includes(framework)),
    [framework]
  )
  const doneCount = activeItems.filter((i) => checked[i.id]).length
  const gaps = activeItems.filter((i) => !checked[i.id])
  const ratio = activeItems.length ? doneCount / activeItems.length : 0
  const band = ratio < 0.4 ? ui.bandLow : ratio < 0.8 ? ui.bandMid : ui.bandHigh

  const toggle = (id: string) =>
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }))

  return (
    <Layout>
      <Seo
        title={p(ui.seoTitle)}
        description={p(ui.seoDescription)}
        path={`${basePath}/tools/identity-compliance-checklist`}
      />
      <Head>
        <link
          rel="alternate"
          hrefLang="en"
          href={`${SITE_URL}/tools/identity-compliance-checklist`}
        />
        <link
          rel="alternate"
          hrefLang="tr"
          href={`${SITE_URL}/tr/tools/identity-compliance-checklist`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${SITE_URL}/tools/identity-compliance-checklist`}
        />
      </Head>
      <JsonLd
        id="checklist-tool"
        data={{
          '@type': 'WebApplication',
          name: 'KVKK & DORA Identity Readiness Checklist',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description: p(ui.seoDescription),
          url: `${SITE_URL}${basePath}/tools/identity-compliance-checklist`,
          publisher: { '@type': 'Organization', name: 'Monofor' },
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <main className="bg-surface text-ink-primary">
        {/* Hero */}
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
            <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
          </div>
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{p(ui.eyebrow)}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {p(ui.title)}
                </Heading>
              </div>
              <div className="mt-8 max-w-2xl">
                <Text size="xl" tone="secondary">
                  {p(ui.lede)}
                </Text>
              </div>
            </div>
          </Container>
        </Section>

        <Section tone="subtle" density="default">
          <Container size="xl">
            <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:gap-12">
              {/* Checklist */}
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
                    {p(ui.frameworkLabel)}
                  </span>
                  <div className="inline-flex rounded-pill border border-line bg-surface p-1">
                    {(['kvkk', 'dora'] as Framework[]).map((f) => (
                      <button
                        key={f}
                        onClick={() => setFramework(f)}
                        className={`rounded-pill px-5 py-1.5 text-body-sm font-medium transition ${
                          framework === f
                            ? 'bg-brand-900 text-white'
                            : 'text-ink-secondary hover:text-ink-primary'
                        }`}
                      >
                        {f.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {activeItems.map((item) => (
                    <label
                      key={item.id}
                      className={`flex cursor-pointer items-start gap-4 rounded-2xl border p-5 transition ${
                        checked[item.id]
                          ? 'border-accent-300 bg-accent-50/60'
                          : 'border-line-subtle bg-surface hover:border-line'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(checked[item.id])}
                        onChange={() => toggle(item.id)}
                        className="mt-1 h-5 w-5 flex-shrink-0 rounded accent-accent-600"
                      />
                      <span>
                        <span className="block text-body-md font-medium text-ink-primary">
                          {p(item.question)}
                        </span>
                        <span className="mt-1 block text-body-sm text-ink-tertiary">
                          {p(ui.whyLabel)}: {p(item.why)}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Score */}
              <div className="lg:sticky lg:top-28 lg:self-start">
                <div className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card">
                  <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
                    {p(ui.scoreHeading)}
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-display-lg font-medium text-ink-primary">
                      {doneCount}
                    </span>
                    <span className="text-display-sm text-ink-tertiary">
                      / {activeItems.length}
                    </span>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-pill bg-surface-muted">
                    <div
                      className={`h-full rounded-pill transition-all duration-300 ${
                        ratio < 0.4
                          ? 'bg-[#ef4444]'
                          : ratio < 0.8
                          ? 'bg-[#f59e0b]'
                          : 'bg-accent-600'
                      }`}
                      style={{ width: `${Math.max(ratio * 100, 4)}%` }}
                    />
                  </div>
                  <Text size="md" tone="secondary" className="mt-4">
                    {p(band)}
                  </Text>
                  <div className="mt-6 flex flex-col gap-2">
                    <Button
                      href={framework === 'kvkk' ? '/compliance/kvkk' : '/compliance/dora'}
                      variant="outline"
                      size="md"
                    >
                      {p(ui.complianceLink)}
                    </Button>
                    <Button href="/contact" variant="primary" size="md">
                      {p(ui.talkCta)}
                    </Button>
                  </div>
                </div>

                {/* Gaps */}
                <div className="mt-4 rounded-3xl border border-line-subtle bg-surface p-8 shadow-card">
                  <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
                    {p(ui.gapsHeading)}
                  </div>
                  {gaps.length === 0 ? (
                    <Text size="md" tone="secondary" className="mt-4">
                      {p(ui.allDone)}
                    </Text>
                  ) : (
                    <ul className="mt-4 space-y-3">
                      {gaps.slice(0, 5).map((gap) => (
                        <li key={gap.id} className="text-body-sm text-ink-secondary">
                          {p(gap.question)}{' '}
                          <Link
                            href={gap.fixHref}
                            className="whitespace-nowrap font-medium text-brand-700 underline-offset-4 hover:underline"
                          >
                            {p(ui.gapCta)} {p(gap.fixLabel)} →
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-10 max-w-3xl text-body-sm text-ink-muted">
              {p(ui.disclaimer)}
            </div>
          </Container>
        </Section>

        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
